import React, { lazy, Suspense, useContext } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { enumsRolesPermissions, enumsTypeUser } from "utils/enums";
import { loaderElement } from "utils/loaderElement";
import Context from "contextAPI/UserContext";
import ProtectedRoute from "routes/ProtectedRoute";
import useAxiosInterceptor from "utils/axiosInterceptor";
import BpmnModels from "components/pages/BpmnModels";
import useLogin from "hooks/useLogin";

const Login = lazy(() => import("components/pages/Login"));
const RecoveryPassword = lazy(() => import("components/pages/RecoveryPassword"));
const ChangePassword = lazy(() => import("components/pages/ChangePassword"));
const ChangePasswordExpiration = lazy(() => import("components/pages/ChangePasswordExpiration"));
const ChangeForgottenPasswordReset = lazy(
  () => import("components/pages/ChangeForgottenPasswordReset"),
);
const ProfilePage = lazy(() => import("components/pages/ProfilePage"));
const ProfileRubricPage = lazy(() => import("components/pages/ProfileRubricPage"));
const Dashboard = lazy(() => import("components/pages/Dashboard"));
const Metrics = lazy(() => import("components/pages/Metrics"));
const MetricsProcessByMonth = lazy(() => import("components/pages/MetricsProcessByMonth"));
const MetricsRecordsByForm = lazy(() => import("components/pages/MetricsRecordsByForm"));
const MetricsRecordByYear = lazy(() => import("components/pages/MetricsRecordsByYear"));
const UserList = lazy(() => import("components/pages/UserList"));
const CreateUser = lazy(() => import("components/pages/CreateUser"));
const Trd = lazy(() => import("components/pages/Trd"));
const CreateList = lazy(() => import("components/pages/CreateList"));
const UserRoles = lazy(() => import("components/pages/UserRoles"));
const WorkGroup = lazy(() => import("components/pages/UserWorkGroup"));
const UserWorkGroupForm = lazy(() => import("components/pages/UserWorkgroupForm"));
const UserWorkGroupMembers = lazy(() => import("components/pages/UserWorkGroupMember"));
const WorkFlow = lazy(() => import("components/pages/WorkFlow"));
const WorkFlowList = lazy(() => import("components/pages/WorkFlowList"));
const UserRolesForm = lazy(() => import("components/pages/UserRolesForm"));
const UserRolesMembers = lazy(() => import("components/pages/UserRolesMembers"));
const UserRolesConfig = lazy(() => import("components/pages/UserRolesConfig"));
const ConsultList = lazy(() => import("components/pages/ConsultList"));
const ItemsList = lazy(() => import("components/pages/ItemsList"));
const TrdExpedients = lazy(() => import("components/pages/TrdExpedients"));
const NotFound = lazy(() => import("components/pages/NotFound"));
const FormsDesigner = lazy(() => import("components/pages/FormsDesigner/FormsDesigner"));
const FormsList = lazy(() => import("components/pages/FormsList"));
const FieldsDesigner = lazy(() => import("components/pages/FieldDesigner"));
const RecordData = lazy(() => import("components/pages/RecordData"));
const RecordFields = lazy(() => import("components/pages/RecordFields"));
const FormFillingBySteps = lazy(() => import("components/pages/FormFillingBySteps"));
const RecordDetail = lazy(() => import("components/pages/RecordDetail"));
const RecordDetails = lazy(() => import("components/pages/RecordDetails"));
const RecordList = lazy(() => import("components/pages/RecordList"));
const RecordListByRecord = lazy(() => import("components/pages/RecordListBYRecord"));
const RecordListByForm = lazy(() => import("components/pages/RecordListByForm"));
const EditList = lazy(() => import("components/pages/EditList"));
const EditItemList = lazy(() => import("components/pages/EditItemList"));
const TrdOrdinationUnits = lazy(() => import("components/pages/TrdOrdinationUnits"));
const EditRecord = lazy(() => import("components/pages/EditRecord"));
const EditExpedients = lazy(() => import("components/pages/EditExpedients"));
const TaskBox = lazy(() => import("components/pages/TaskBox"));
const ProcessDetail = lazy(() => import("components/pages/ProcessDetail"));
const WorkFlowEdit = lazy(() => import("components/pages/WorkFlowEdit"));
const WorkFlowSteps = lazy(() => import("components/pages/WorkFlowSteps"));
const WorkFlowConfigurationSteps = lazy(
  () => import("components/pages/WorkFlowConfigurationSteps"),
);
const WorkflowView = lazy(() => import("components/pages/WorkflowView"));
const Integrations = lazy(() => import("components/pages/Integrations"));
const DetailIntegrations = lazy(() => import("components/pages/DetailIntegrations"));
const RequestIntegration = lazy(() => import("components/pages/RequestIntegration"));
const ElectronicSignature = lazy(() => import("components/pages/ElectronicSignature"));
const BiometricSignature = lazy(() => import("components/pages/BiometricSignature"));
const ReportElectronicSignatures = lazy(
  () => import("components/pages/ReportElectronicSignatures"),
);
const Signatories = lazy(() => import("components/pages/Signatories"));
const InvoicesIntegrationConfigCreate = lazy(
  () => import("components/pages/InvoicesIntegrationConfigCreate"),
);
const InvoicesIntegrationConfigEdit = lazy(
  () => import("components/pages/InvoicesIntegrationConfigEdit"),
);
const WorkflowsTraceability = lazy(() => import("components/pages/WorkflowsTraceability"));
const FilterForm = lazy(() => import("components/pages/FilterForm"));
const ConsultConfigurationFields = lazy(
  () => import("components/pages/ConsultConfigurationFields"),
);
const ChangeOrderFields = lazy(() => import("components/pages/ChangeOrderFields"));
const ChangeUserStatus = lazy(() => import("components/pages/ChangeUserStatus"));
const DocumentaryTypes = lazy(() => import("components/pages/DocumentaryTypes"));
const UserLog = lazy(() => import("components/pages/UserLog"));
const TrdExpedientsConsultation = lazy(() => import("components/pages/TrdExpedientsConsultation"));
const DocumentaryTypesEdition = lazy(() => import("components/pages/DocumentaryTypesEdition"));
const NotFoundFile = lazy(() => import("components/pages/NotFoundFile/NotFoundFile"));
const Parameters = lazy(() => import("components/pages/Parameters"));
const ProcessManagement = lazy(
  () => import("components/pages/ProcessManagement/ProcessManagement"),
);
const TrashConsult = lazy(() => import("components/pages/TrashConsult"));
const AuditLogs = lazy(() => import("components/pages/AuditLogs"));
const ExportData = lazy(() => import("components/pages/ExportData/ExportData"));
const CreateUserApi = lazy(() => import("components/pages/CreateUserApi"));
const CreateUserApiPageForm = lazy(() => import("components/pages/CreateUserApiPageForm"));
const PDFViewer = lazy(() => import("components/pages/PDFViewerPage"));
const DocumentTemplatesCreatePage = lazy(
  () => import("components/pages/DocumentTemplatesCreatePage"),
);
const DocumentTemplatesConsult = lazy(() => import("components/pages/DocumentTemplatesConsult"));
const ProcessCompleteConsult = lazy(() => import("components/pages/ProcessMetricsConsult"));
const ProcessInCourseConsult = lazy(() => import("components/pages/ProcessMetricsConsult"));
const ProcessInterruptedConsult = lazy(() => import("components/pages/ProcessMetricsConsult"));

const App = () => {
  const {
    userType,
    rolesPermissionsGranted,
    designerForms,
    designerFormsPermissions,
    workflowAllForms,
    workflowFormsPermissions,
  } = useContext(Context);
  const { isLogged, isPasswordExpired } = useLogin();
  useAxiosInterceptor();

  // CONTROL AND PERMISSIONS
  const isAdmin = isLogged === true && userType === enumsTypeUser.ADMIN;
  const isStandard = isLogged === true && userType === enumsTypeUser.STANDARD;

  const hasRolePermission = (permission) => {
    if (rolesPermissionsGranted === null) {
      return false;
    }
    return rolesPermissionsGranted.includes(permission);
  };

  const access = isLogged === true && userType !== enumsTypeUser.QUERY;
  const accessTRD =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.TRD_CONSFIGURATION));
  const accessDocumentaryType =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.DOCUMENTAL_TYPES));
  const accessConfigurationApp =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.APPLICATION_SETUP));
  const accessDesignerForms = isAdmin || (isStandard && designerForms === true);
  const accessDesignerFormsConsult =
    isStandard && Array.isArray(designerFormsPermissions) && designerFormsPermissions.length !== 0;
  const accessWorkflowsForms = isAdmin || (isStandard && workflowAllForms === true);
  const accessWorkflowsFormsConsult =
    isStandard && Array.isArray(workflowFormsPermissions) && workflowFormsPermissions.length !== 0;
  const accessList =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.LIST_DESIGNER));
  const accessUserManager =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.USER_MANAGER));
  const accessRoles =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.ROLE_MANAGER));
  const accessAdminThirdPartyAccounts =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.THIRD_PARTY_ACCOUNT));
  const accessIntegrations = isAdmin;
  const accessWorkGroups =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.WORK_GROUP_MANAGER));
  const accessProcessManagement =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.PROCESS_MANAGEMENT));
  const accessMetrics = isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.METRICS));
  const accessAuditLogs =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.AUDIT_LOG));
  const accessTemplateDesigner =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.TEMPLATE_DESIGNER));

  const permissionContext = {
    isLogged,
    isAdmin,
    isStandard,
    access,
    accessTRD,
    accessDocumentaryType,
    accessConfigurationApp,
    accessDesignerForms,
    accessDesignerFormsConsult,
    accessWorkflowsForms,
    accessWorkflowsFormsConsult,
    accessList,
    accessUserManager,
    accessRoles,
    accessAdminThirdPartyAccounts,
    accessIntegrations,
    accessWorkGroups,
    accessProcessManagement,
    accessMetrics,
    accessAuditLogs,
    accessTemplateDesigner,
  };

  const routeAccess = {
    managed: permissionContext.access,
    trd: permissionContext.access && permissionContext.accessTRD,
    documentaryTypes: permissionContext.access && permissionContext.accessDocumentaryType,
    appConfiguration: permissionContext.access && permissionContext.accessConfigurationApp,
    processManagement:
      permissionContext.isLogged &&
      permissionContext.access &&
      permissionContext.accessProcessManagement,
    formsDesignerCreate:
      permissionContext.accessDesignerForms &&
      permissionContext.accessDesignerFormsConsult === false,
    formsDesignerList:
      (permissionContext.access && permissionContext.accessDesignerForms) ||
      (permissionContext.access && permissionContext.accessDesignerFormsConsult),
    listDesigner: permissionContext.access && permissionContext.accessList,
    userManager: permissionContext.access && permissionContext.accessUserManager,
    userManagerOnly: permissionContext.accessUserManager,
    workGroups: permissionContext.access && permissionContext.accessWorkGroups,
    thirdPartyAccounts: permissionContext.access && permissionContext.accessAdminThirdPartyAccounts,
    roles: permissionContext.access && permissionContext.accessRoles,
    metrics: permissionContext.access && permissionContext.accessMetrics,
    auditLogs: permissionContext.access && permissionContext.accessAuditLogs,
    workflowDesignerCreate:
      permissionContext.access &&
      permissionContext.accessWorkflowsForms &&
      permissionContext.accessWorkflowsFormsConsult === false,
    workflowDesignerList:
      (permissionContext.access && permissionContext.accessWorkflowsForms) ||
      (permissionContext.access && permissionContext.accessWorkflowsFormsConsult),
    templateDesigner: permissionContext.access && permissionContext.accessTemplateDesigner,
    integrations: permissionContext.access && permissionContext.accessIntegrations,
  };

  return (
    <Router>
      <Suspense fallback={<div className="content-centered">{loaderElement(true)}</div>}>
        <Switch>
          {/*------------ URL'S LOGUIN, PASSWORD */}

          <ProtectedRoute exact path="/" component={Login} accessCondition={isLogged === false} />
          <ProtectedRoute
            exact
            path="/user/recoverpassword"
            component={RecoveryPassword}
            accessCondition={isLogged === false}
          />
          <ProtectedRoute
            exact
            path="/nueva-contrasena"
            component={ChangePassword}
            accessCondition={isLogged === false}
          />
          <ProtectedRoute
            exact
            path="/restablecimiento-de-contrasena"
            component={ChangeForgottenPasswordReset}
            accessCondition={isLogged === false}
          />
          <ProtectedRoute
            exact
            path="/renew-password"
            component={ChangePasswordExpiration}
            accessCondition={isPasswordExpired || isLogged}
          />

          <ProtectedRoute
            exact
            path="/profile"
            component={ProfilePage}
            accessCondition={isLogged === true}
          />

          <ProtectedRoute
            exact
            path="/profile/rubric"
            component={ProfileRubricPage}
            accessCondition={isLogged === true}
          />

          {/*------------ URL'S EXPORT DATA */}

          <ProtectedRoute
            exact
            path="/export-data"
            component={ExportData}
            accessCondition={isLogged === true}
          />

          <ProtectedRoute
            exact
            path="/export-data/process"
            component={ExportData}
            accessCondition={routeAccess.metrics}
          />

          {/*------------ URL'S ACCESS TRD */}

          <ProtectedRoute exact path="/trd" component={Trd} accessCondition={routeAccess.trd} />

          <ProtectedRoute
            exact
            path="/trd/ordination/:id?/:backgroundId?"
            component={TrdOrdinationUnits}
            accessCondition={routeAccess.trd}
          />
          <ProtectedRoute
            exact
            path="/trd/expedient/:id?/:backgroundId?"
            component={TrdExpedients}
            accessCondition={routeAccess.trd}
          />
          <ProtectedRoute
            exact
            path="/application/config/parameters"
            component={Parameters}
            accessCondition={routeAccess.appConfiguration}
          />
          <ProtectedRoute
            exact
            path="/trd/expedients/edit/:expedientId"
            component={EditExpedients}
            accessCondition={routeAccess.trd}
          />
          <ProtectedRoute
            exact
            path="/documentary-types/list"
            component={DocumentaryTypes}
            accessCondition={routeAccess.documentaryTypes}
          />
          <ProtectedRoute
            exact
            path="/documentary-types/list/:documentaryId/:documentaryName"
            component={DocumentaryTypesEdition}
            accessCondition={routeAccess.documentaryTypes}
          />

          {/*------------- URL'S DASHBOARD */}

          <ProtectedRoute
            exact
            path="/dashboard"
            component={Dashboard}
            accessCondition={isLogged === true}
          />

          {/*------------- URL'S RECORD LIST FORMS */}

          <ProtectedRoute
            exact
            path="/record/list/forms"
            component={RecordData}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/create/:id"
            component={RecordFields}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/create/wizard/:id/:hash"
            component={FormFillingBySteps}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/details/:recordId/:id"
            component={RecordDetails}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/details/:recordId/:id/:fileId"
            component={RecordDetails}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/edit/:id/:recordId"
            component={EditRecord}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/edit/:id/:recordId/:query"
            component={EditRecord}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/traceability/record/:id/:recordId"
            component={WorkflowsTraceability}
            accessCondition={isLogged === true}
          />

          {/*------------- URL'S RECORD CONSULT LIST FORMS */}

          <ProtectedRoute
            exact
            path="/record/list/form"
            component={RecordListByForm}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/list/table/:id"
            component={RecordList}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/list/forms/filters/:id"
            component={FilterForm}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/list/forms/filters/:id/:query"
            component={FilterForm}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/detail/:recordId/:id/:query(true|false)"
            component={RecordDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/detail/:recordId/:id/:fileId"
            component={RecordDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/detail/:recordId/:id/:fileId/:query"
            component={RecordDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/detail/:recordId/:id"
            component={RecordDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/trd/expedients/consultation"
            component={TrdExpedientsConsultation}
            accessCondition={isLogged === true}
          />

          <ProtectedRoute
            exact
            path="/pdf-viewer"
            component={PDFViewer}
            accessCondition={isLogged === true}
          />

          {/*----------- URL'S CONSULT BY RECORD */}

          <ProtectedRoute
            exact
            path="/record/list/record"
            component={RecordListByRecord}
            accessCondition={isLogged === true}
          />

          {/*----------- URL'S UPLOAD FILES AND SIGNATORIES */}

          <ProtectedRoute
            exact
            path="/files/signature/:formId/:hash"
            component={ElectronicSignature}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/signatories/:id"
            component={Signatories}
            accessCondition={isLogged === true}
          />

          <ProtectedRoute
            exact
            path="/files/biosignature/:formId/:hash"
            component={BiometricSignature}
            accessCondition={isLogged === true}
          />

          {/*----------- URL'S DEATILRECORDPROCESS */}

          <ProtectedRoute
            exact
            path="/traceability/workflow/:id/:recordId"
            component={WorkflowsTraceability}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/process/detail/:id/:processId"
            component={ProcessDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/process/management"
            component={ProcessManagement}
            accessCondition={routeAccess.processManagement}
          />
          <ProtectedRoute
            exact
            path="/taskBox/detail/:id"
            component={ProcessDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/taskBox"
            component={TaskBox}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/taskBox/:param"
            component={TaskBox}
            accessCondition={isLogged === true}
          />

          {/*------------- URL'S REPORT SIGNATURES */}

          <ProtectedRoute
            exact
            path="/reports/signatures"
            component={ReportElectronicSignatures}
            accessCondition={isLogged === true}
          />

          {/*--------- URL'S TRASH BIN */}

          <ProtectedRoute
            exact
            path="/trash"
            component={TrashConsult}
            accessCondition={isLogged === true}
          />

          {/*------------- URL'S FORMSDESIGNER */}

          <ProtectedRoute
            exact
            path="/formsdesigner/create"
            component={FormsDesigner}
            accessCondition={routeAccess.formsDesignerCreate}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/list"
            component={FormsList}
            accessCondition={routeAccess.formsDesignerList}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/edit/:id"
            component={FormsDesigner}
            accessCondition={routeAccess.managed}
          />

          {/*----------- URL'S FORMSDESIGNER FIELDS */}

          <ProtectedRoute
            exact
            path="/formsdesigner/fields/:id/"
            component={FieldsDesigner}
            accessCondition={routeAccess.managed}
          />
          <ProtectedRoute
            exact
            path="/configuration/edit/:id/:form_uuid/:hashRecord/"
            component={ConsultConfigurationFields}
            accessCondition={routeAccess.managed}
          />
          <ProtectedRoute
            exact
            path="/configuration/order/:id/:hash/:formId"
            component={ChangeOrderFields}
            accessCondition={routeAccess.managed}
          />
          <ProtectedRoute
            exact
            path="/configuration/consult/:id/:form_uuid/:status/:hashRecord"
            component={ConsultConfigurationFields}
            accessCondition={routeAccess.managed}
          />

          {/*------------ URL'S CREATELIST */}

          <ProtectedRoute
            exact
            path="/formsdesigner/createlist"
            component={CreateList}
            accessCondition={routeAccess.listDesigner}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/consultlist"
            component={ConsultList}
            accessCondition={routeAccess.listDesigner}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/editlist/:id"
            component={EditList}
            accessCondition={routeAccess.listDesigner}
          />

          {/*----------- URL'S ITEMSlIST */}

          <ProtectedRoute
            exact
            path="/formsdesigner/itemslist/:id/:parentId?"
            component={ItemsList}
            accessCondition={routeAccess.listDesigner}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/editItem/list/:listId/itemslist/:id"
            component={EditItemList}
            accessCondition={routeAccess.listDesigner}
          />

          {/*----------- URL'S ADMINUSERS */}

          <ProtectedRoute
            exact
            path="/user/create"
            component={CreateUser}
            accessCondition={routeAccess.userManager}
          />
          <ProtectedRoute
            exact
            path="/user/list"
            component={UserList}
            accessCondition={routeAccess.userManager}
          />
          <ProtectedRoute
            exact
            path="/user/edit/:id"
            component={CreateUser}
            accessCondition={routeAccess.userManager}
          />
          <ProtectedRoute
            exact
            path="/user/edit/status/:id"
            component={ChangeUserStatus}
            accessCondition={routeAccess.userManager}
          />
          <ProtectedRoute
            exact
            path="/user/log/:id"
            component={UserLog}
            accessCondition={routeAccess.userManagerOnly}
          />
          <ProtectedRoute
            exact
            path="/user/workgroup"
            component={WorkGroup}
            accessCondition={routeAccess.workGroups}
          />
          <ProtectedRoute
            exact
            path="/user/create/workgroup"
            component={UserWorkGroupForm}
            accessCondition={routeAccess.workGroups}
          />
          <ProtectedRoute
            exact
            path="/user/workgroup/members/:id/:statusGroup"
            component={UserWorkGroupMembers}
            accessCondition={routeAccess.workGroups}
          />
          <ProtectedRoute
            exact
            path="/user/create/workgroup/:id"
            component={UserWorkGroupForm}
            accessCondition={routeAccess.workGroups}
          />
          <ProtectedRoute
            exact
            path="/user/Third-party-accounts"
            component={CreateUserApi}
            accessCondition={routeAccess.thirdPartyAccounts}
          />
          <ProtectedRoute
            exact
            path="/user/Third-party-accounts/create"
            component={CreateUserApiPageForm}
            accessCondition={routeAccess.thirdPartyAccounts}
          />

          {/*---------- URL'S ROLES */}

          <ProtectedRoute
            exact
            path="/user/roles"
            component={UserRoles}
            accessCondition={routeAccess.roles}
          />
          <ProtectedRoute
            exact
            path="/user/create/roles"
            component={UserRolesForm}
            accessCondition={routeAccess.roles}
          />
          <ProtectedRoute
            exact
            path="/user/roles/edit/:id"
            component={UserRolesForm}
            accessCondition={routeAccess.roles}
          />
          <ProtectedRoute
            exact
            path="/user/roles/members/:id"
            component={UserRolesMembers}
            accessCondition={routeAccess.roles}
          />
          <ProtectedRoute
            exact
            path="/user/roles/config/:id/:hash"
            component={UserRolesConfig}
            accessCondition={routeAccess.roles}
          />

          {/*--------- URL'S METRICS */}

          <ProtectedRoute
            exact
            path="/metrics"
            component={Metrics}
            accessCondition={routeAccess.metrics}
          />
          <ProtectedRoute
            exact
            path="/metrics/recordListbyForm"
            component={MetricsRecordsByForm}
            accessCondition={routeAccess.managed}
          />
          <ProtectedRoute
            exact
            path="/audit/logs"
            component={AuditLogs}
            accessCondition={routeAccess.auditLogs}
          />
          <ProtectedRoute
            exact
            path="/metrics/recordListByYear"
            component={MetricsRecordByYear}
            accessCondition={routeAccess.managed}
          />
          <ProtectedRoute
            exact
            path="/metrics/proccessbymonth"
            component={MetricsProcessByMonth}
            accessCondition={routeAccess.managed}
          />

          {/*------------- URL'S WORKFLOWDESIGNER */}

          <ProtectedRoute
            exact
            path="/workflow"
            component={WorkFlow}
            accessCondition={routeAccess.workflowDesignerCreate}
          />
          <ProtectedRoute
            exact
            path="/workflow/designer/:id"
            component={BpmnModels}
            accessCondition={routeAccess.workflowDesignerCreate}
          />
          <ProtectedRoute
            exact
            path="/workflow/list"
            component={WorkFlowList}
            accessCondition={routeAccess.workflowDesignerList}
          />
          <ProtectedRoute
            exact
            path="/workflow/edit/:id"
            component={WorkFlowEdit}
            accessCondition={routeAccess.managed}
          />
          <ProtectedRoute
            exact
            path="/workflow/steps/:id/:hash/:formId"
            component={WorkFlowSteps}
            accessCondition={routeAccess.managed}
          />
          <ProtectedRoute
            exact
            path="/workflow/view/:model/:hash"
            component={WorkflowView}
            accessCondition={routeAccess.managed}
          />

          <ProtectedRoute
            exact
            path="/metrics/process-complete/:workflow_name/:workflow_id"
            component={ProcessCompleteConsult}
            accessCondition={routeAccess.managed}
          />

          <ProtectedRoute
            exact
            path="/metrics/process-inProgress/:workflow_name/:workflow_id"
            component={ProcessInCourseConsult}
            accessCondition={routeAccess.managed}
          />

          <ProtectedRoute
            exact
            path="/metrics/process-interrupted/:workflow_name/:workflow_id"
            component={ProcessInterruptedConsult}
            accessCondition={routeAccess.managed}
          />

          {/*------------- URL'S TEMPLATES DESIGNER*/}

          <ProtectedRoute
            exact
            path="/templates/create"
            component={DocumentTemplatesCreatePage}
            accessCondition={routeAccess.templateDesigner}
          />

          <ProtectedRoute
            exact
            path="/templates/list"
            component={DocumentTemplatesConsult}
            accessCondition={routeAccess.templateDesigner}
          />

          <ProtectedRoute
            exact
            path="/templates/edit/:idTemplate"
            component={DocumentTemplatesCreatePage}
            accessCondition={routeAccess.templateDesigner}
          />

          {/*------------- URL'S WORKFLOWDESIGNER STEPS */}

          <ProtectedRoute
            exact
            path="/workflow/steps/configuration/:id/:workflowid/:hash/:formId"
            component={WorkFlowConfigurationSteps}
            accessCondition={routeAccess.managed}
          />

          {/*--------- URL'S INTEGRATIONS */}

          <ProtectedRoute
            exact
            path="/integrations"
            component={Integrations}
            accessCondition={routeAccess.integrations}
          />
          <ProtectedRoute
            exact
            path="/integrations/:id"
            component={DetailIntegrations}
            accessCondition={routeAccess.integrations}
          />
          <ProtectedRoute
            exact
            path="/integrations/request/:id"
            component={RequestIntegration}
            accessCondition={routeAccess.integrations}
          />

          <ProtectedRoute
            exact
            path="/integrations/:id/create-config/invoices"
            component={InvoicesIntegrationConfigCreate}
            accessCondition={routeAccess.integrations}
          />

          <ProtectedRoute
            exact
            path="/integrations/:id/edit-config/invoices"
            component={InvoicesIntegrationConfigEdit}
            accessCondition={routeAccess.integrations}
          />

          {/*---------- URL NOTFOUND */}

          <ProtectedRoute
            exact
            path="/file-not-found"
            component={NotFoundFile}
            accessCondition={isLogged === true}
          />
          <Route exact path="*" component={NotFound} />
        </Switch>
      </Suspense>
    </Router>
  );
};

export default App;
